// PlasmaWaves shader adapted from Shadertoy-style implementation

const frag = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_speed;
uniform float u_seed;

// Parameters
uniform float u_scale;                 // scene scale
uniform float u_min_line_width;        // min linewidth in scene space
uniform float u_max_line_width;        // max linewidth in scene space
uniform float u_line_speed;            // line movement speed
uniform float u_line_amplitude;        // line amplitude
uniform float u_line_frequency;        // line frequency

uniform float u_warp_speed;            // turbulence speed
uniform float u_warp_frequency;        // turbulence frequency
uniform float u_warp_amplitude;        // turbulence amplitude

uniform float u_offset_frequency;      // offset grouping frequency
uniform float u_offset_speed;          // offset speed
uniform float u_min_offset_spread;     // min offset spread
uniform float u_max_offset_spread;     // max offset spread

// Colors: 0=bg left, 1=bg right, 2=line color
uniform vec3 u_color_0;
uniform vec3 u_color_1;
uniform vec3 u_color_2;

varying vec2 vUv;

// Grid helpers (kept for possible debug overlay)
const float gridSmoothWidth = 0.015;
const float axisWidth = 0.05;
const float majorLineWidth = 0.025;
const float minorLineWidth = 0.0125;
const float majorLineFrequency = 5.0;
const float minorLineFrequency = 1.0;

// Fixed loop bound for WebGL1
const int LINES_PER_GROUP = 16;

#define drawCircle(pos, radius, coord) smoothstep(radius + gridSmoothWidth, radius, length(coord - (pos)))
#define drawSmoothLine(pos, halfWidth, t) smoothstep(halfWidth, 0.0, abs(pos - (t)))
#define drawCrispLine(pos, halfWidth, t) smoothstep(halfWidth + gridSmoothWidth, halfWidth, abs(pos - (t)))
#define drawPeriodicLine(freq, width, t) drawCrispLine(freq / 2.0, width, abs(mod(t, freq) - (freq) / 2.0))

float drawGridLines(float axis) {
  return   drawCrispLine(0.0, axisWidth, axis)
         + drawPeriodicLine(majorLineFrequency, majorLineWidth, axis)
         + drawPeriodicLine(minorLineFrequency, minorLineWidth, axis);
}

float drawGrid(vec2 space) {
  return min(1.0, drawGridLines(space.x) + drawGridLines(space.y));
}

// Fourier-like 1D random
float random1(float t) {
  return (cos(t) + cos(t * 1.3 + 1.3) + cos(t * 1.4 + 1.4)) / 3.0;
}

float getPlasmaY(float x, float horizontalFade, float offset, float time) {
  return random1(x * u_line_frequency + time * u_line_speed) * horizontalFade * u_line_amplitude + offset;
}

void main() {
  vec2 fragCoord = gl_FragCoord.xy;
  vec2 uv = fragCoord / u_resolution.xy;
  float time = u_time * u_speed;

  vec2 space = (fragCoord - u_resolution.xy * 0.5) / u_resolution.x * 2.0 * u_scale;
  
  float horizontalFade = 1.0 - (cos(uv.x * 6.28318530718) * 0.5 + 0.5);
  float verticalFade   = 1.0 - (cos(uv.y * 6.28318530718) * 0.5 + 0.5);

  // Nonlinear turbulence
  space.y += random1(space.x * u_warp_frequency + time * u_warp_speed) * u_warp_amplitude * (0.5 + horizontalFade);
  space.x += random1(space.y * u_warp_frequency + time * u_warp_speed + 2.0) * u_warp_amplitude * horizontalFade;

  vec3 lines = vec3(0.0);
  vec3 lineColor = u_color_2;

  for (int l = 0; l < LINES_PER_GROUP; l++) {
    float normalizedLineIndex = float(l) / float(LINES_PER_GROUP);
    float offsetTime = time * u_offset_speed;
    float offsetPosition = float(l) + space.x * u_offset_frequency;
    float randVal = random1(offsetPosition + offsetTime) * 0.5 + 0.5;
    float halfWidth = mix(u_min_line_width, u_max_line_width, randVal * horizontalFade) * 0.5;

    float offset = random1(offsetPosition + offsetTime * (1.0 + normalizedLineIndex)) *
                   mix(u_min_offset_spread, u_max_offset_spread, horizontalFade);

    float linePosition = getPlasmaY(space.x, horizontalFade, offset, time);
    float line = drawSmoothLine(linePosition, halfWidth, space.y) * 0.5
               + drawCrispLine(linePosition, halfWidth * 0.15, space.y);

    float circleX = mod(float(l) + time * u_line_speed, 25.0) - 12.0;
    vec2 circlePosition = vec2(circleX, getPlasmaY(circleX, horizontalFade, offset, time));
    float circle = drawCircle(circlePosition, 0.01, space) * 4.0;

    line = line + circle;
    lines += lineColor * randVal * line;
  }

  vec3 bg = mix(u_color_0, u_color_1, uv.x) * verticalFade;
  vec3 col = bg + lines;
  
  gl_FragColor = vec4(col, 1.0);
}
`;

const shader = {
  name: 'PlasmaWaves',
  shader: {
    frag,
  },
  schema: [
    {
      key: 'colors',
      type: 'color-palette',
      label: 'Colors (bgL,bgR,line)',
      min: 3,
      max: 6,
      default: ['#1f3a7a', '#86a9ff', '#3f80ff'],
    },
    {
      key: 'u_scale',
      type: 'number',
      label: 'Scale',
      min: 0.5,
      max: 20,
      step: 0.1,
      default: 5,
    },

    {
      key: 'u_min_line_width',
      type: 'number',
      label: 'Min Line Width',
      min: 0.001,
      max: 0.2,
      step: 0.001,
      default: 0.02,
    },
    {
      key: 'u_max_line_width',
      type: 'number',
      label: 'Max Line Width',
      min: 0.05,
      max: 1.5,
      step: 0.01,
      default: 0.5,
    },
    {
      key: 'u_line_amplitude',
      type: 'number',
      label: 'Line Amplitude',
      min: 0,
      max: 3,
      step: 0.01,
      default: 1.0,
    },
    {
      key: 'u_line_frequency',
      type: 'number',
      label: 'Line Frequency',
      min: 0,
      max: 2,
      step: 0.01,
      default: 0.2,
    },
    {
      key: 'u_line_speed',
      type: 'number',
      label: 'Line Speed',
      min: 0,
      max: 3,
      step: 0.01,
      default: 0.2,
    },

    {
      key: 'u_warp_amplitude',
      type: 'number',
      label: 'Warp Amplitude',
      min: 0,
      max: 3,
      step: 0.01,
      default: 1.0,
    },
    {
      key: 'u_warp_frequency',
      type: 'number',
      label: 'Warp Frequency',
      min: 0,
      max: 3,
      step: 0.01,
      default: 0.5,
    },
    {
      key: 'u_warp_speed',
      type: 'number',
      label: 'Warp Speed',
      min: 0,
      max: 1,
      step: 0.001,
      default: 0.04,
    },

    {
      key: 'u_offset_frequency',
      type: 'number',
      label: 'Offset Frequency',
      min: 0,
      max: 3,
      step: 0.01,
      default: 0.5,
    },
    {
      key: 'u_offset_speed',
      type: 'number',
      label: 'Offset Speed',
      min: 0,
      max: 1,
      step: 0.001,
      default: 0.266,
    },
    {
      key: 'u_min_offset_spread',
      type: 'number',
      label: 'Min Offset Spread',
      min: 0,
      max: 3,
      step: 0.01,
      default: 0.6,
    },
    {
      key: 'u_max_offset_spread',
      type: 'number',
      label: 'Max Offset Spread',
      min: 0,
      max: 5,
      step: 0.01,
      default: 2.0,
    },

    { key: 'u_seed', type: 'number', label: 'Seed', min: 0, max: 9999, step: 1, default: 0 },
    {
      key: 'u_speed',
      type: 'number',
      label: 'Global Speed',
      min: 0,
      max: 10,
      step: 0.1,
      default: 1,
    },
    { key: 'animated', type: 'boolean', label: 'Animated', default: true },
  ],
  examples: [
    'dynamic-shader(PlasmaWaves, colors=#1f3a7a,#86a9ff,#3f80ff; u_scale=5; u_line_speed=0.2; animated=true)',
    'dynamic-shader(PlasmaWaves, colors=#120f2b,#4169e1,#6eb5ff; u_scale=6; u_warp_amplitude=0.8; u_line_frequency=0.25; animated=true)',
  ],
};

export default shader;
